import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { toast } from '@/hooks/errors/use-toast';
import PersistentFavoriteButton from '@/components/ui/PersistentFavoriteButton';
import { Volume2, VolumeX } from 'lucide-react';
import GlassPill from '@/components/ui/GlassPill';

interface VideoDisplayProps {
  videoUrl: string;
  title?: string;
  description?: string;
  videoId: string;
  duration?: number;
  hasAudio?: boolean;
  initialLikeCount: number;
  isLiked: boolean;
  hasPrevious?: boolean;
  hasNext?: boolean;
  onPrevious?: (e?: React.MouseEvent) => void;
  onNext?: (e?: React.MouseEvent) => void;
  onWheel?: (e: React.WheelEvent) => void;
  onLike?: () => void;
  showLikeButton?: boolean;
  showDescription?: boolean;
  dropdownLabel?: string;
}

export const VideoDisplay: React.FC<VideoDisplayProps> = ({
  videoUrl,
  title,
  description,
  videoId,
  duration,
  hasAudio,
  initialLikeCount,
  isLiked,
  hasPrevious = false,
  hasNext = false,
  onPrevious,
  onNext,
  onWheel,
  onLike,
  showLikeButton = true,
  showDescription = true,
  dropdownLabel
}) => {
  // Like state for PersistentFavoriteButton
  const [isFavorited, setIsFavorited] = useState(isLiked);
  const [favoriteCount, setFavoriteCount] = useState(initialLikeCount);
  
  // Reset favorite count when video changes
  useEffect(() => {
    setFavoriteCount(initialLikeCount);
    setIsFavorited(isLiked);
  }, [videoId, initialLikeCount, isLiked]);

  // Handle favorite toggle for PersistentFavoriteButton
  const handleFavoriteToggle = useCallback(async (itemId: string): Promise<void> => {
    try {
      if (onLike) {
        await onLike();
      }
      
      // Update local state for immediate feedback
      const newFavoriteState = !isFavorited;
      const newCount = newFavoriteState ? favoriteCount + 1 : Math.max(0, favoriteCount - 1);
      
      setIsFavorited(newFavoriteState);
      setFavoriteCount(newCount);
      
      toast({
        title: newFavoriteState ? "Added to favorites" : "Removed from favorites",
        description: `Video ${newFavoriteState ? 'saved to' : 'removed from'} your favorites.`,
        variant: "success"
      });
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast({
        title: "Favorite action failed",
        description: error instanceof Error ? error.message : "Unable to update favorites. Please try again.",
        variant: "error"
      });
    }
  }, [onLike, isFavorited, favoriteCount]);

  // Navigation with proper event handling
  const handleNavigation = (direction: 'prev' | 'next', e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
      if (e.nativeEvent) e.nativeEvent.stopImmediatePropagation();
    }

    try {
      if (direction === 'prev' && onPrevious) {
        onPrevious(e);
      } else if (direction === 'next' && onNext) {
        onNext(e);
      }
    } catch (error) {
      console.error('Navigation error:', error);
      toast({
        title: "Navigation failed",
        description: "Unable to navigate to the next video. Please try again.",
        variant: "error"
      });
    }
  };

  // Format duration
  const formatDuration = (seconds?: number) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div 
      className="relative flex-1 flex flex-col h-full bg-gradient-to-br from-purple-50/30 to-pink-50/30 dark:from-purple-900/20 dark:to-pink-900/20"
      onWheel={onWheel}
    >
      {/* Main video container */}
      <div className="relative flex-1 flex items-center justify-center p-4 md:p-0 min-h-0">
        {/* Top-left actions container */}
        <AnimatePresence mode="wait">
          {videoId && (
            <motion.div
              key={`actions-${videoId}`}
              className="absolute top-4 left-4 z-40 flex items-center gap-2"
              initial={{ opacity: 0, scale: 0.8, y: -20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: -20 }}
              transition={{ 
                type: "spring", 
                stiffness: 400, 
                damping: 25,
                delay: 0.3
              }}
            >
                            {showLikeButton && (
                <PersistentFavoriteButton
                  itemId={videoId}
                  onToggleFavorite={handleFavoriteToggle}
                  isFavorite_prop={isFavorited}
                  favoriteCount_prop={favoriteCount}
                  forceWhiteText={true}
                  data-test-id="favorite-button"
                />
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Hover-based navigation zones */}
        <AnimatePresence mode="wait">
          {hasPrevious && onPrevious && (
            <motion.div 
              key="nav-previous"
              className="absolute left-0 top-0 w-[15%] h-full cursor-pointer z-30 group select-none" 
              onClick={(e) => handleNavigation('prev', e)}
              onMouseDown={(e) => e.preventDefault()}
              data-test-id="left-nav-zone"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <motion.div 
                className="absolute left-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-90 transition-all duration-200 scale-90 group-hover:scale-100"
              >
                <motion.div 
                  className="rounded-full h-12 w-12 flex items-center justify-center bg-black/30 backdrop-filter backdrop-blur-2xl shadow-lg group-hover:bg-black/40"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m15 18-6-6 6-6"/>
                  </svg>
                </motion.div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence mode="wait">
          {hasNext && onNext && (
            <motion.div 
              key="nav-next"
              className="absolute right-0 top-0 w-[15%] h-full cursor-pointer z-30 group select-none" 
              onClick={(e) => handleNavigation('next', e)}
              onMouseDown={(e) => e.preventDefault()}
              data-test-id="right-nav-zone"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <motion.div 
                className="absolute right-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-90 transition-all duration-200 scale-90 group-hover:scale-100"
              >
                <motion.div 
                  className="rounded-full h-12 w-12 flex items-center justify-center bg-black/30 backdrop-filter backdrop-blur-2xl shadow-lg group-hover:bg-black/40"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m9 18 6-6-6-6"/>
                  </svg>
                </motion.div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Video player */}
        <motion.video
          key={`video-${videoId}`}
          src="/videos/big-bear.mp4"
          controls
          className={cn(
            "object-contain rounded-lg shadow-lg bg-black",
            // Mobile: current perfect sizing
            "max-w-full max-h-full",
            // Desktop: large sizing like ProfileAvatar with minimum sizes
            "md:max-h-[95vh] md:max-w-[calc(95vw-250px)] md:w-auto md:h-auto md:rounded-none md:shadow-none md:min-w-[500px] md:min-h-[500px]"
          )}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.3 }}
        />


      </div>


    </div>
  );
}; 